import { gql, TypedDocumentNode } from "@apollo/client";
import { ADDRESS_FRAGMENT, PHONE_NUMBER_FRAGMENT } from "@/fragments";
import type { Address, FacilityOperatingStatus, FacilityType, PhoneNumber } from "@/types";

export type MergePurgeEmployment = {
  id: string;
  title: string | null;
  formattedTitle: string | null;
  person: { id: string; displayName: string } | null;
};

export type MergePurgeJobPost = {
  id: string;
  name: string | null;
  stage: string | null;
};

export type MergePurgeFacility = {
  id: string;
  name: string;
  legacyId: string | null;
  archivedAt: string | null;
  facilityType: FacilityType | null;
  operatingStatus: FacilityOperatingStatus | null;
  website: string | null;
  managementGroup: string | null;
  section: { id: string; name: string } | null;
  chapter: { id: string; name: string; code: string } | null;
  mainAddress: Address | null;
  mailingAddress: Address | null;
  phone: PhoneNumber | null;
  parentCompany: { id: string; name: string } | null;
  employments: { totalCount: number; nodes: MergePurgeEmployment[] } | null;
};

export type MergePurgeFacilityQueryResponse = {
  facility: MergePurgeFacility;
};

export type MergePurgeFacilityQueryVariables = {
  id: string;
};

export const MERGE_PURGE_FACILITY_QUERY: TypedDocumentNode<
  MergePurgeFacilityQueryResponse,
  MergePurgeFacilityQueryVariables
> = gql`
  query MergePurgeFacility($id: ID!) {
    facility(id: $id) {
      id
      name
      legacyId
      archivedAt
      facilityType
      operatingStatus
      website
      managementGroup
      section {
        id
        name
      }
      chapter {
        id
        name
        code
      }
      mainAddress {
        ...AddressFragment
      }
      mailingAddress {
        ...AddressFragment
      }
      phone {
        ...PhoneNumberFragment
      }
      parentCompany {
        id
        name
      }
      employments(active: true, first: 10) {
        totalCount
        nodes {
          id
          title
          formattedTitle
          person {
            id
            displayName
          }
        }
      }
    }
  }
  ${ADDRESS_FRAGMENT}
  ${PHONE_NUMBER_FRAGMENT}
`;

export type MergePurgeFacilityJobPostsQueryResponse = {
  jobPosts: { totalCount: number; nodes: MergePurgeJobPost[] } | null;
};

export type MergePurgeFacilityJobPostsQueryVariables = {
  facilityId: string;
};

export const MERGE_PURGE_FACILITY_JOB_POSTS_QUERY: TypedDocumentNode<
  MergePurgeFacilityJobPostsQueryResponse,
  MergePurgeFacilityJobPostsQueryVariables
> = gql`
  query MergePurgeFacilityJobPosts($facilityId: ID!) {
    jobPosts(
      first: 10
      input: {
        facilityId: $facilityId
        status: ["In Progress", "Expired"]
        stage: ["Draft", "Pending", "In Review", "Assist", "Active"]
      }
    ) {
      totalCount
      nodes {
        id
        name
        stage
      }
    }
  }
`;
